export const CHARACTERIZATION_VIEW_ZONES = "zones";
export const CHARACTERIZATION_VIEW_ALL_REPORTS = "allReports";

let resolvedMode = null;

export const normalizeCharacterizationViewMode = (value) =>
  value === CHARACTERIZATION_VIEW_ALL_REPORTS
    ? CHARACTERIZATION_VIEW_ALL_REPORTS
    : CHARACTERIZATION_VIEW_ZONES;

export const extractSettingsDocument = (payload) => {
  const settings = payload?.settings ?? payload;
  if (Array.isArray(settings)) return settings[0] || null;
  return settings || null;
};

export const getResolvedCharacterizationViewMode = () => resolvedMode;

export const writeCharacterizationViewMode = (mode) => {
  const next = normalizeCharacterizationViewMode(mode);
  resolvedMode = next;
  try {
    localStorage.setItem("characterizationViewMode", next);
  } catch {
    // Ignore storage failures (private mode, quota, etc.)
  }
  return next;
};

export const rememberCharacterizationViewModeFromSettings = (settings) =>
  writeCharacterizationViewMode(settings?.characterizationViewMode);

export const readCharacterizationViewMode = () => {
  try {
    return normalizeCharacterizationViewMode(
      localStorage.getItem("characterizationViewMode"),
    );
  } catch {
    return CHARACTERIZATION_VIEW_ZONES;
  }
};

export const characterizationLandingPath = (mode) =>
  mode === CHARACTERIZATION_VIEW_ALL_REPORTS
    ? "/characterization/list"
    : "/characterization-areas";

export const getCharacterizationDetailBackTarget = ({
  zone,
  listSearch = "",
  fromAllReports = false,
  superContainer,
  containers,
} = {}) => {
  const search = listSearch || "";
  if (fromAllReports) {
    return { path: `/characterization/list${search}`, state: {} };
  }
  if (zone?._id) {
    return {
      path: `/characterization/list/${zone._id}${search}`,
      state: { zone, superContainer, containers },
    };
  }
  return null;
};

export const isCharacterizationNavActive = (pathname) =>
  pathname === "/characterization-areas" ||
  pathname.startsWith("/characterization/");

export const formatCharacterizationZoneLabel = (site) => {
  const address = String(site?.addressName || "").trim();
  const positioning = site?.positioning
    ? `${String(site.positioning).charAt(0).toUpperCase()}${String(site.positioning).slice(1)}`
    : "";
  if (address && positioning) return `${address} — ${positioning}`;
  return address || positioning || "";
};

export const matchSiteForBulkId = (sites, bulkId) => {
  const id = String(bulkId || "");
  if (!id) return null;
  const list = Array.isArray(sites) ? sites : [];
  return (
    list.find((site) => String(site.superContainerId || "") === id) ||
    list.find((site) => String(site._id) === id) ||
    null
  );
};

export const resolveSuperContainerForZoneSite = (superContainers, zoneSite) => {
  const list = Array.isArray(superContainers) ? superContainers : [];
  const superId = zoneSite?.superContainerId;
  if (superId) {
    const byId = list.find((item) => String(item?._id) === String(superId));
    if (byId) return byId;
  }
  const zoneId = zoneSite?._id;
  if (!zoneId) return null;
  return (
    list.find((item) =>
      (item?.containerObjectIdList || []).some(
        (id) => String(id) === String(zoneId),
      ),
    ) || null
  );
};

export const resolvePeriodMaterialRange = (period) => ({
  periodMaterialStart:
    period?.periodMaterialStartSubfilter ?? period?.start ?? null,
  periodMaterialStop:
    period?.periodMaterialStopSubfilter ?? period?.stop ?? null,
});

const toShiftedIso = (value, deltaMs) => {
  if (value == null || value === "") return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Date(date.getTime() + deltaMs).toISOString();
};

export const resolveClientRegisteredInfoRange = ({
  period,
  historyMaterial,
  historyId = 0,
} = {}) => {
  const { periodMaterialStart, periodMaterialStop } =
    resolvePeriodMaterialRange(period);
  if (periodMaterialStart && periodMaterialStop) {
    const start = toShiftedIso(periodMaterialStart, -1);
    const stop = toShiftedIso(periodMaterialStop, 1);
    if (start && stop) {
      return { start, stop };
    }
  }
  const historyTime =
    historyMaterial?.[historyId]?._time ?? historyMaterial?.[0]?._time;
  const start = toShiftedIso(historyTime, -1);
  const stop = toShiftedIso(historyTime, 1);
  if (!start || !stop) return null;
  return { start, stop };
};

/** Same window as the dashboard L/R characterization dedupe. */
const ALL_REPORTS_DEDUP_MS = 2 * 60 * 1000;

const dedupeAllReportPeriods = (periods, windowMs = ALL_REPORTS_DEDUP_MS) => {
  const kept = [];
  const sorted = [...(periods || [])].sort(
    (left, right) =>
      new Date(right.start).getTime() - new Date(left.start).getTime(),
  );

  sorted.forEach((period) => {
    if (!period?.start) return;
    const startMs = new Date(period.start).getTime();
    if (Number.isNaN(startMs)) return;
    const groupId = String(period.groupId || period.zoneId || "");

    const duplicateIndex = kept.findIndex((existing) => {
      const existingGroup = String(existing.groupId || existing.zoneId || "");
      if (existingGroup !== groupId) return false;
      return Math.abs(new Date(existing.start).getTime() - startMs) < windowMs;
    });

    if (duplicateIndex < 0) {
      kept.push(period);
      return;
    }

    const duplicate = kept[duplicateIndex];
    const duplicateHasClient =
      Boolean(duplicate.weight) || Boolean(duplicate.client);
    const periodHasClient = Boolean(period.weight) || Boolean(period.client);
    if (!duplicateHasClient && periodHasClient) {
      kept[duplicateIndex] = period;
    }
  });

  return kept.sort(
    (left, right) =>
      new Date(right.start).getTime() - new Date(left.start).getTime(),
  );
};

/**
 * Attach per-zone period responses to their site and collapse L/R duplicates.
 * `groups` is `{ target, periods }[]` from one request per non-mobile member.
 */
export const collectAllReportPeriods = (groups, sites) => {
  const rows = (Array.isArray(groups) ? groups : []).flatMap(
    ({ target, periods }) => {
      const bulkId = target?.superContainerId || target?.containerId;
      const site =
        matchSiteForBulkId(sites, bulkId) || target?.container || null;
      const groupId = String(
        target?.superContainerId ||
          target?.zoneId ||
          target?.containerId ||
          site?._id ||
          "",
      );
      return (Array.isArray(periods) ? periods : []).map((period) => {
        const { periodMaterialStart, periodMaterialStop } =
          resolvePeriodMaterialRange(period);
        return {
          ...period,
          periodMaterialStartSubfilter: periodMaterialStart,
          periodMaterialStopSubfilter: periodMaterialStop,
          zoneSite: site,
          zoneId: site?._id || target?.containerId,
          zoneLabel: formatCharacterizationZoneLabel(site),
          groupId,
        };
      });
    },
  );
  return dedupeAllReportPeriods(rows);
};
